/**
 * SupplierMatchOpportunityBoundary (AJIP-16.02.008 · WS22)
 * ---------------------------------------------------------------------
 * Interface contract for Match / Opportunity recommendations offered to
 * the Supplier Journey by external recommendation providers.
 *
 * This module is:
 *   - Validation-only.
 *   - Provider-independent.
 *   - Free of external-system imports.
 *   - Free of storage access.
 *   - Free of customer-facing behaviour.
 *   - Without authority over eligibility, activation, progression or
 *     completion.
 *
 * Recommendations are non-authoritative metadata. AI may recommend;
 * AI shall never determine, activate, complete or progress a journey.
 */
import { SupplierMatchOpportunityBoundaryError } from './SupplierJourneyErrors';

const MATCH_PROVIDER_METHODS = Object.freeze([
  'recommendOpportunities',
  'describeOpportunityRecommendation',
]);

const OPPORTUNITY_PROVIDER_METHODS = Object.freeze([
  'listOpenOpportunities',
  'describeOpportunity',
]);

const assertMatchOpportunityProvider = (candidate) => {
  if (!candidate || typeof candidate !== 'object') {
    throw new SupplierMatchOpportunityBoundaryError(
      'MatchOpportunityProvider: candidate must be non-null object',
    );
  }
  for (const m of MATCH_PROVIDER_METHODS) {
    if (typeof candidate[m] !== 'function') {
      throw new SupplierMatchOpportunityBoundaryError(
        `MatchOpportunityProvider: missing method '${m}'`,
      );
    }
  }
  return true;
};

const assertOpportunityProvider = (candidate) => {
  if (!candidate || typeof candidate !== 'object') {
    throw new SupplierMatchOpportunityBoundaryError(
      'OpportunityProvider: candidate must be non-null object',
    );
  }
  for (const m of OPPORTUNITY_PROVIDER_METHODS) {
    if (typeof candidate[m] !== 'function') {
      throw new SupplierMatchOpportunityBoundaryError(
        `OpportunityProvider: missing method '${m}'`,
      );
    }
  }
  return true;
};

const BOUNDARY_GOVERNANCE = Object.freeze({
  ai_may: 'recommend|describe',
  ai_shall_never: 'determine|activate|complete|progress|score-authoritatively',
});

const BOUNDARY_NAME = 'SupplierMatchOpportunityBoundary';

export {
  BOUNDARY_NAME,
  BOUNDARY_GOVERNANCE,
  MATCH_PROVIDER_METHODS,
  OPPORTUNITY_PROVIDER_METHODS,
  assertMatchOpportunityProvider,
  assertOpportunityProvider,
};
